import { memo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../../shared/ui/EmptyState.js';
import { ErrorState } from '../../../../shared/ui/ErrorState.js';
import { SkeletonLoader } from '../../../../shared/ui/SkeletonLoader.js';
import { panelLead, panelTitle } from '../../../../shared/ui/panelHeader.styles.js';
import type { BaseChartProps } from '../../types/analytics.types.js';

const chartShellClass =
  'an-card flex flex-col gap-3 border-[var(--color-rail-card-border,var(--color-an-border))] shadow-sm transition-[box-shadow,border-color] hover:shadow-md focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[var(--color-an-primary)]';

function ChartHeader({
  title,
  subtitle,
  titleId,
  descriptionId,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly titleId?: string;
  readonly descriptionId?: string;
}): JSX.Element {
  return (
    <header className="flex flex-col gap-0.5">
      <h3 id={titleId} className={panelTitle({ className: 'text-base' })}>
        {title}
      </h3>
      {subtitle != null && subtitle.length > 0 ? (
        <p id={descriptionId} className={panelLead({ className: 'text-xs' })}>
          {subtitle}
        </p>
      ) : null}
    </header>
  );
}

/**
 * Base chart component with consistent styling, accessibility, and error handling
 * Provides loading, error, and empty states
 */
export const BaseChart = memo<BaseChartProps & { children?: ReactNode }>(
  ({
    isLoading,
    error,
    emptyMessage,
    emptyHint,
    title,
    subtitle,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    children,
  }) => {
    const { t } = useTranslation('admin');
    const resolvedEmptyMessage = emptyMessage ?? t('analytics.charts.insufficientData.zero');

    const chartId = `chart-${title.toLowerCase().replace(/\s+/g, '-')}`;
    const descriptionId = `${chartId}-description`;

    if (isLoading) {
      return (
        <div className={chartShellClass} role="region" aria-label={ariaLabel || title}>
          <ChartHeader title={title} subtitle={subtitle} />
          <SkeletonLoader type="chart" />
        </div>
      );
    }

    if (error) {
      return (
        <div className={chartShellClass} role="region" aria-label={ariaLabel || title}>
          <ChartHeader title={title} subtitle={subtitle} />
          <ErrorState error={error} />
        </div>
      );
    }

    if (!children) {
      return (
        <div className={chartShellClass} role="region" aria-label={ariaLabel || title}>
          <ChartHeader title={title} subtitle={subtitle} />
          <EmptyState
            icon="📊"
            title={resolvedEmptyMessage}
            message={resolvedEmptyMessage}
            hint={emptyHint}
          />
        </div>
      );
    }

    return (
      <div
        className={chartShellClass}
        role="region"
        aria-label={ariaLabel || title}
        aria-describedby={ariaDescribedBy || descriptionId}
      >
        <ChartHeader
          title={title}
          subtitle={subtitle}
          titleId={chartId}
          descriptionId={descriptionId}
        />
        <div className="relative min-h-[200px] max-md:min-h-[150px]" role="img" aria-labelledby={chartId}>
          {children}
        </div>
      </div>
    );
  },
);

BaseChart.displayName = 'BaseChart';
